import { useState, type CSSProperties, type DragEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AmountByCurrency } from "./AmountByCurrency";

/**
 * Kolona table, ista na porudžbinama i na ponudama.
 *
 * Do sada je svaki ekran crtao svoju. Razlike nisu bile stvar ukusa nego greške koje su se
 * razišle: na ponudama je klizala cela stranica pa je zaglavlje kolone odlazilo sa njom i nije se
 * videlo u čemu gledaš, a na porudžbinama je zbir sabirao različite valute i označavao ih valutom
 * prve kartice.
 *
 * Zaglavlje ide u DVA reda. U jedan red je stalo „Od…", „Otpreml…", „Fakt…" — naziv statusa je
 * prvo što treba da se vidi, a odsecao se da bi iznos stao pored njega.
 */
export function KanbanColumn({
  naziv,
  boja,
  broj,
  eur,
  poValutama,
  bezKursa,
  jezik,
  moze,
  onDrop,
  opis,
  children,
}: {
  naziv: string;
  /** Boja statusa; nosi je traka na vrhu i tačka uz naziv. */
  boja: string;
  broj: number;
  /** Zbir preračunat u evre; prazno kad kolona nema iznos. */
  eur?: number | null;
  /** Zbir po valutama, kako stoji na dokumentima. */
  poValutama?: Record<string, number>;
  /**
   * How many documents of the column are in a currency without any exchange rate. They are in the
   * per-currency amounts but not in the euro sum, so the header says the euro sum is short of them.
   */
  bezKursa?: number;
  jezik: string;
  /** Sme li ono što se trenutno vuče da padne u ovu kolonu. */
  moze?: boolean;
  onDrop?: () => void;
  /** Opis kolone za čitač ekrana; bez njega se čita samo naziv. */
  opis?: string;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const [iznad, setIznad] = useState(false);

  return (
    <div
      role="group"
      aria-label={opis ?? naziv}
      style={{
        ...kolona,
        ...(moze && iznad ? { borderColor: boja, background: nijansa(boja) } : {}),
      }}
      onDragOver={(e: DragEvent) => {
        if (!moze) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (!iznad) setIznad(true);
      }}
      onDragLeave={(e: DragEvent) => {
        // Prelazak na dete kolone nije izlazak iz nje.
        if (e.currentTarget.contains(e.relatedTarget as Node)) return;
        setIznad(false);
      }}
      onDrop={(e: DragEvent) => {
        e.preventDefault();
        setIznad(false);
        if (moze) onDrop?.();
      }}
    >
      <header style={{ ...zaglavlje, borderTopColor: boja }}>
        <div style={prviRed}>
          <span aria-hidden style={{ ...tacka, background: boja }} />
          <strong style={naslov} title={naziv}>{naziv}</strong>
          <span style={brojka}>{broj}</span>
        </div>
        {eur != null && (
          <div style={drugiRed}>
            <AmountByCurrency eur={eur} byCurrency={poValutama} locale={jezik} />
          </div>
        )}
        {!!bezKursa && bezKursa > 0 && (
          <div style={vanZbira}>
            {t("amount.noRate", { count: bezKursa, defaultValue: "Not in the EUR total: {{count}} (no exchange rate)" })}
          </div>
        )}
      </header>
      <div style={telo}>{children}</div>
    </div>
  );
}

/** Ista boja, samo providna: podloga kolone iznad koje se nešto drži. */
function nijansa(boja: string): string {
  return `color-mix(in srgb, ${boja} 8%, transparent)`;
}

const kolona: CSSProperties = {
  flex: "1 1 220px", minWidth: 220, maxWidth: 330,
  background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: 10,
  display: "flex", flexDirection: "column",
  // Kolona ima svoju visinu i sama kliza, da bi joj zaglavlje ostalo na mestu. Bez toga kliza
  // cela stranica i naziv kolone odlazi sa vrha.
  maxHeight: "calc(100vh - 300px)",
  transition: "border-color .12s ease, background .12s ease",
};

const zaglavlje: CSSProperties = {
  display: "flex", flexDirection: "column", gap: 4,
  padding: "10px 12px",
  borderTop: "3px solid", borderTopLeftRadius: 10, borderTopRightRadius: 10,
  borderBottom: "1px solid var(--border)", background: "var(--surface)",
  flex: "none",
};

const prviRed: CSSProperties = { display: "flex", alignItems: "center", gap: 8, minWidth: 0 };
const drugiRed: CSSProperties = { display: "flex", alignItems: "center", paddingLeft: 17 };
const vanZbira: CSSProperties = { paddingLeft: 17, fontSize: 11, lineHeight: 1.3, color: "var(--warn)" };

const tacka: CSSProperties = { width: 9, height: 9, borderRadius: "50%", flex: "none" };

/** Naziv sme da pređe u drugi red umesto da se odseče: on je ono zbog čega kolona i postoji. */
const naslov: CSSProperties = {
  fontSize: 13, color: "var(--ink)", lineHeight: 1.25, flex: "1 1 auto", minWidth: 0,
  overflowWrap: "anywhere",
};

const brojka: CSSProperties = {
  fontSize: 11, fontWeight: 700, color: "var(--ink-3)",
  background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: 999,
  padding: "1px 8px", flex: "none", fontVariantNumeric: "tabular-nums",
};

const telo: CSSProperties = {
  display: "flex", flexDirection: "column", gap: 8, padding: 10,
  overflowY: "auto", flex: 1, minHeight: 40,
};

/** Vodoravni klizač table; isti na oba ekrana. */
export const boardScroll: CSSProperties = {
  display: "flex", gap: 12, overflowX: "auto", paddingBottom: 8, alignItems: "flex-start",
};
